import * as React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { inject } from 'mobx-react';

import { AnalyticsStore, RouteStore } from 'bernie-core';
import { ErrorBoundary, Viewport, ViewSmall, ViewLarge } from 'bernie-view';

import {
  UitkCardAloha,
  UitkCardAlohaContentSection,
  UitkCardAlohaLink,
  UitkCardAlohaFeaturedHeader
} from 'uitk-react-cards';
import { UitkLayoutFlex, UitkLayoutFlexItem } from 'uitk-react-layout-flex';
import { UitkLayoutGrid, UitkLayoutGridItem } from 'uitk-react-layout-grid';
import { UitkHeading } from 'uitk-react-text';

import { trackAnalytics, focusElement, trackAnalyticsList } from 'utility';
import { Journey, PricingInformation, FlightBadges } from '.';
import { Observer } from 'components/shared';
import { DetailsAndFaresDialog } from 'components/shared/details-and-fares';
import { AnalyticsType } from 'types/shared';
import { JourneyType } from 'types/search/listings/journey';
import { StandardOfferType } from 'types/search/listings/offer';
import { FlightAction, FlightsStandardOffer } from '__generated__/api';
import FlightsJourneyAvailableFaresInformation = FlightsStandardOffer.FlightsJourneyAvailableFaresInformation;

export interface OfferProps {
  offer: StandardOfferType;
  offerIndex: number;
  selectedOfferRef: React.Ref<HTMLButtonElement>;
  handleForcedChoiceModal?: (fsiUrl?: string, packageableUrl?: string) => void;
  openForcedChoiceModal?: boolean;
  lastOfferViewAnalytics?: AnalyticsType;
  updateOnViewedAnalytics?: (anaytics: AnalyticsType, offerIndex: number) => void;
  analytics?: AnalyticsStore;
  routeStore?: RouteStore;
  openDetails: (offerIndex: number, availableFaresInformation: FlightsJourneyAvailableFaresInformation) => void;
  selectedOfferIndex: number;
  handleOfferSelect: (selectOfferAction: FlightAction.Fragment) => void;
}

@inject('routeStore')
export class Offer extends React.Component<OfferProps> {
  private fslRef;
  private readonly offerRef;
  public constructor(props: OfferProps) {
    super(props);
    this.fslRef = React.createRef();
    this.offerRef = React.createRef();
  }

  public analyticsOnOfferSelect = () => {
    const { offer, analytics } = this.props;
    trackAnalyticsList(offer.selectOfferAction.analyticsList, analytics);
  };

  public openSheet = (event: React.MouseEvent<HTMLButtonElement>) => {
    const { offer, offerIndex, openDetails } = this.props;
    const availableFaresInformation = offer.journeys[0]
      .flightsJourneyAvailableFaresInformation as FlightsJourneyAvailableFaresInformation;
    this.analyticsOnOfferSelect();
    openDetails(offerIndex, availableFaresInformation);
    if (this.fslRef && this.fslRef.current) {
      this.fslRef.current.dispatchEvent(new CustomEvent('fsl-click'));
    }
    event.preventDefault();
  };

  public closeDialog = () => {
    focusElement(this.offerRef);
  };

  public intersectionAction = () => {
    const {
      offer: { flightsOfferAnalytics },
      lastOfferViewAnalytics,
      updateOnViewedAnalytics,
      offerIndex,
      analytics
    } = this.props;
    analytics && lastOfferViewAnalytics && trackAnalytics(lastOfferViewAnalytics, analytics);
    updateOnViewedAnalytics(flightsOfferAnalytics.onViewedAnalytics, offerIndex);
  };

  public render() {
    const {
      offer: {
        accessibilityHeading,
        accessibilityMessage,
        pinnedOfferMessage,
        journeys,
        pricingInformation: { price, mainPrice, subPrice, taxesLabel, tripType },
        badges,
        sponsoredAirline
      },
      offerIndex,
      selectedOfferRef,
      openForcedChoiceModal,
      routeStore,
      selectedOfferIndex,
      handleOfferSelect
    } = this.props;

    const detailsAndFaresDialogId = `details-and-fares-dialog-${offerIndex}`;
    const isOfferSelected = selectedOfferIndex === offerIndex;
    const availableFaresInformation = journeys[0].flightsJourneyAvailableFaresInformation;
    const hygieneAmenitiesPresentation = journeys[0].hygieneAmenitiesPresentation;

    return (
      <Observer intersectionAction={this.intersectionAction}>
        <UitkCardAloha className="all-b-margin-three" selected={isOfferSelected}>
          {accessibilityHeading && (
            <span className="is-visually-hidden">
              <UitkHeading level={3} size={300}>
                {accessibilityHeading}
              </UitkHeading>
            </span>
          )}
          {pinnedOfferMessage && <UitkCardAlohaFeaturedHeader title={{ tag: 'h3', text: pinnedOfferMessage }} />}
          {sponsoredAirline && (
            <ErrorBoundary label="meso.fsl.ad">
              <meso-native-fsl-backend
                class="meso-native-fsl"
                uci="fsl"
                ref={this.fslRef}
                imageurl={journeys[0].airlines[0].image.url}
                airlineName={journeys[0].airlines[0].text}
                airlineSponsoredText={sponsoredAirline.sponsoredText}
                airlineSponsoredHeader={sponsoredAirline.sponsoredHeader}
                impressionTrackingURL={sponsoredAirline.delayedImpressionURL}
                clickTrackingURL={sponsoredAirline.clickTrackingURL}
              />
            </ErrorBoundary>
          )}
          <UitkCardAlohaContentSection>
            {badges && <FlightBadges badges={badges} />}
            <UitkLayoutGrid columns={12} space="two" className="all-t-margin-two">
              <>
                <UitkLayoutGridItem colSpan={{ small: 8, large: 9 }}>
                  {journeys.map((journey: JourneyType, journeyIndex: number) => (
                    <div key={`offer-${offerIndex}-journey-${journeyIndex}`}>
                      <Journey journey={journey} />
                    </div>
                  ))}
                </UitkLayoutGridItem>
                <UitkLayoutGridItem colSpan={{ small: 4, large: 3 }}>
                  <div>
                    <UitkLayoutFlex wrap="nowrap">
                      <>
                        <UitkLayoutFlexItem grow={1}>
                          <div>
                            <PricingInformation
                              price={price}
                              mainPrice={mainPrice}
                              subPrice={subPrice}
                              taxesLabel={taxesLabel}
                              tripType={tripType}
                              urgencyMessage={journeys[0].urgencyMessage}
                              urgencyAccessibilityMessage={journeys[0].urgencyAccessibilityMessage}
                            />
                          </div>
                        </UitkLayoutFlexItem>
                      </>
                    </UitkLayoutFlex>
                  </div>
                </UitkLayoutGridItem>
              </>
            </UitkLayoutGrid>
            <Viewport>
              <ViewSmall>
                <UitkCardAlohaLink>
                  <RouterLink
                    to={{
                      pathname: routeStore.pathname,
                      search: routeStore.mergeQueryString(routeStore.search, {
                        pwaDialog: detailsAndFaresDialogId
                      })
                    }}
                    role="button"
                    ref={this.offerRef}
                    data-test-id="select-link"
                    onClick={this.analyticsOnOfferSelect}
                  >
                    {accessibilityMessage}
                  </RouterLink>
                </UitkCardAlohaLink>
              </ViewSmall>
              <ViewLarge>
                <UitkCardAlohaLink>
                  <button ref={isOfferSelected && selectedOfferRef} onClick={this.openSheet} data-test-id="select-link">
                    {accessibilityMessage}
                  </button>
                </UitkCardAlohaLink>
              </ViewLarge>
            </Viewport>
          </UitkCardAlohaContentSection>
          <ViewSmall>
            <DetailsAndFaresDialog
              onDialogClose={this.closeDialog}
              openForcedChoiceModal={openForcedChoiceModal}
              handleOfferSelect={handleOfferSelect}
              showSummaryAndDetails={true}
              detailsAndFaresDialogId={detailsAndFaresDialogId}
              availableFaresInformation={availableFaresInformation}
              hygieneAmenitiesPresentation={hygieneAmenitiesPresentation}
              itemIndex={0}
            />
          </ViewSmall>
        </UitkCardAloha>
      </Observer>
    );
  }
}
